import type { ReactNode } from 'react';
import { AlertTriangle, Inbox, Loader2, SearchX } from 'lucide-react';
import { T } from '@/design/tokens';
import { Button } from './Button';

/** Vazio de verdade: nunca existiu registro. Diferente de "a busca não achou". */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="state">
      <Inbox size={22} color={T.inkFaint} aria-hidden />
      <div className="state__title">{title}</div>
      {description && <div className="state__text">{description}</div>}
      {action && <div className="state__action">{action}</div>}
    </div>
  );
}

/** O filtro/busca não encontrou nada — o caminho de saída é limpar o filtro. */
export function NoResultsState({ onClear }: { onClear?: () => void }) {
  return (
    <div className="state">
      <SearchX size={22} color={T.inkFaint} aria-hidden />
      <div className="state__title">Nenhum resultado para esta busca</div>
      <div className="state__text">Tente outro termo ou remova os filtros aplicados.</div>
      {onClear && (
        <div className="state__action">
          <Button variant="outline" small onClick={onClear}>
            Limpar filtros
          </Button>
        </div>
      )}
    </div>
  );
}

export function LoadingState({ label = 'Carregando…' }: { label?: string }) {
  return (
    <div className="state" role="status" aria-live="polite">
      <Loader2 size={22} color={T.inkFaint} className="spin" aria-hidden />
      <div className="state__title">{label}</div>
    </div>
  );
}

/**
 * Erro para o operador: frase em pt-BR + ação de recuperação.
 * Nunca stack trace. O requestId aparece pequeno, para abrir chamado.
 */
export function ErrorState({
  title = 'Não foi possível carregar',
  message,
  requestId,
  onRetry,
}: {
  title?: string;
  message: string;
  requestId?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="state state--error" role="alert">
      <AlertTriangle size={22} color={T.red} aria-hidden />
      <div className="state__title" style={{ color: T.red }}>
        {title}
      </div>
      <div className="state__text">{message}</div>
      {onRetry && (
        <div className="state__action">
          <Button variant="outline" small onClick={onRetry}>
            Tentar novamente
          </Button>
        </div>
      )}
      {requestId && (
        <div className="state__text mono" style={{ marginTop: 12, fontSize: 11 }}>
          código: {requestId}
        </div>
      )}
    </div>
  );
}

export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="table-wrap" aria-hidden>
      <table className="table">
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columns }).map((__, columnIndex) => (
                <td key={columnIndex}>
                  <div
                    className="skeleton"
                    style={{ height: 12, width: columnIndex === 0 ? '70%' : '45%' }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
